import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { AppointmentListItem } from '@/hooks/useAppointments';
import type { CalendarAppointmentMeta } from '@/hooks/useCalendarAppointmentMeta';
import type { GroupSessionListItem } from '@/hooks/useGroupSessions';
import { CalendarOverlapOverflow } from '@/components/appointments/CalendarOverlapOverflow';
import { AppointmentCalendarCard } from './AppointmentCalendarCard';
import {
  CALENDAR_COLUMN_HEADER_PX,
  CALENDAR_DAY_COLUMN_MIN_PX,
  CALENDAR_STAFF_HEADER_PX,
  appointmentJournalRailColor,
  CALENDAR_CLOSED_OVERLAY_CLASS,
  calendarBlockLineVisibility,
  calendarInitialScrollTop,
  calendarNowLineTop,
  clinicColorBlockAppearance,
  closedHourOverlays,
  isCalendarWeekend,
  practitionerColumnNameLines,
  timedBlockGeometry,
} from '@/utils/appointmentCalendarStyle';
import {
  appointmentDateLocale,
  formatAppointmentTimeRange,
  formatHourLabel,
} from '@/utils/appointmentTimeFormat';
import {
  appointmentToInterval,
  appointmentsInStaffColumn,
  calendarDayKey,
  groupByCalendarDay,
  layoutOverlappingAppointments,
  overlapBoxStyle,
  type OverlapPlacement,
} from '@/utils/appointmentOverlapLayout';
import { appointmentsInResourceColumn } from '@/utils/appointmentResourceCalendar';
import { slotStartMinutesFromOffset } from '@/utils/calendarSlotClick';
import {
  CLINIC_TIMEZONE,
  clinicDayKey,
  clinicDayNumber,
  clinicInstantFromMinutes,
  clinicJsWeekday,
  clinicWeekdayIndex,
  sameClinicDay,
} from '@/utils/calendarClinicTime';
import {
  allDayColumnBox,
  calendarBlockTitle,
  isAllDayCalendarBlock,
} from '@/utils/calendarTimeBlock';
import { practitionerDateHoursKey } from '@/lib/practitionerAvailability';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const SLOT_MINUTES = 30;
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export interface DragData {
  kind: 'reschedule' | 'resize-end';
  appointment: AppointmentListItem;
}

export interface DropData {
  date: string;
  resourceId?: string;
}

export interface CalendarStaffColumn {
  id: string;
  name: string;
  subtitle?: string;
}

export type CalendarColumnPerspective = 'practitioner' | 'resource';

interface DayHours {
  enabled: boolean;
  start: string;
  end: string;
  periods?: Array<{ start: string; end: string }>;
  source?: 'weekly' | 'exception';
  exceptionType?: string | null;
}

function startOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

function dayKey(d: Date) {
  return calendarDayKey(d);
}

export function CalendarGrid({
  days,
  appointments,
  groupSessions,
  conflictAptId,
  isDnDActive,
  staffColumns,
  columnPerspective = 'practitioner',
  resourceIdsByAppointment,
  showClinicianOnCards = false,
  unassignedLabel,
  metaById,
  bookingHours,
  hourHeight,
  dayStartHour,
  dayEndHour,
  openingHoursMidpoint,
  openingHoursReady = true,
  openingHoursContextKey,
  onSlotClick,
  onSelectDay,
  onSlotDragCreate,
  onAppointmentClick,
  onGroupSessionClick,
  resolvedHoursByDate,
  resolvedHoursByPractitionerDate,
  onDayHoursClick,
  columnHoursByDate,
  onColumnDayHoursClick,
  hoursColumnId,
}: {
  days: Date[];
  appointments: AppointmentListItem[];
  groupSessions: GroupSessionListItem[];
  conflictAptId: string | null;
  isDnDActive: boolean;
  staffColumns: CalendarStaffColumn[];
  columnPerspective?: CalendarColumnPerspective;
  resourceIdsByAppointment?: Map<string, string[]>;
  showClinicianOnCards?: boolean;
  unassignedLabel?: string;
  metaById: Map<string, CalendarAppointmentMeta>;
  bookingHours: Map<number, DayHours | null>;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
  openingHoursMidpoint: number;
  openingHoursReady?: boolean;
  openingHoursContextKey?: string;
  onSlotClick: (start: Date, resourceId?: string, point?: { x: number; y: number }) => void;
  onSelectDay?: (date: Date) => void;
  onSlotDragCreate: (
    start: Date,
    end: Date,
    resourceId?: string,
    point?: { x: number; y: number },
  ) => void;
  onAppointmentClick: (id: string) => void;
  onGroupSessionClick: (id: string) => void;
  resolvedHoursByDate?: Map<string, DayHours | null>;
  resolvedHoursByPractitionerDate?: Map<string, DayHours | null>;
  onDayHoursClick?: (date: Date) => void;
  columnHoursByDate?: Map<string, Map<string, DayHours>>;
  onColumnDayHoursClick?: (date: Date, columnId: string) => void;
  hoursColumnId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const today = useMemo(() => startOfDay(new Date()), []);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(tick);
  }, []);
  const hours = useMemo(
    () =>
      Array.from({ length: Math.max(1, dayEndHour - dayStartHour) }, (_, i) => dayStartHour + i),
    [dayStartHour, dayEndHour],
  );

  const mouseDownRef = useRef(false);
  const [quickSel, setQuickSel] = useState<{
    dayIso: string;
    startMin: number;
    endMin: number;
    resourceId?: string;
  } | null>(null);
  const quickSelRef = useRef(quickSel);
  quickSelRef.current = quickSel;
  const onSlotClickRef = useRef(onSlotClick);
  const onSlotDragCreateRef = useRef(onSlotDragCreate);
  onSlotClickRef.current = onSlotClick;
  onSlotDragCreateRef.current = onSlotDragCreate;

  useEffect(() => {
    function handleMouseUp(event: MouseEvent) {
      if (!mouseDownRef.current) return;
      mouseDownRef.current = false;
      const selection = quickSelRef.current;
      quickSelRef.current = null;
      setQuickSel(null);
      if (!selection) return;
      const start = clinicInstantFromMinutes(new Date(selection.dayIso), selection.startMin);
      const end = clinicInstantFromMinutes(new Date(selection.dayIso), selection.endMin);
      const point = { x: event.clientX, y: event.clientY };
      if (selection.endMin > selection.startMin) {
        onSlotDragCreateRef.current(start, end, selection.resourceId, point);
      } else {
        onSlotClickRef.current(start, selection.resourceId, point);
      }
    }
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const byDay = useMemo(() => groupByCalendarDay(appointments), [appointments]);

  const groupSessionsByDay = useMemo(() => groupByCalendarDay(groupSessions), [groupSessions]);

  const locale = appointmentDateLocale(i18n.language);
  const allocationByAppointment = resourceIdsByAppointment ?? new Map<string, string[]>();
  const hasUnassigned =
    columnPerspective === 'resource'
      ? appointments.some((appointment) => {
          const ids = allocationByAppointment.get(appointment.id);
          return !ids || ids.length === 0;
        })
      : appointments.some((appointment) => !appointment.clinician_id);
  const columns: CalendarStaffColumn[] =
    staffColumns.length > 0 && hasUnassigned
      ? [
          ...staffColumns,
          {
            id: 'unassigned',
            name: unassignedLabel ?? t('appointments.unassigned'),
          },
        ]
      : staffColumns;
  const splitByStaff = columns.length > 0;
  const hasColumnSubtitles = columns.some((column) => Boolean(column.subtitle));
  const stickyHeaderPx =
    CALENDAR_COLUMN_HEADER_PX +
    (splitByStaff ? CALENDAR_STAFF_HEADER_PX + (hasColumnSubtitles ? 12 : 0) : 0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollContextKey = [
    openingHoursReady ? 'ready' : 'pending',
    openingHoursContextKey ?? '',
    openingHoursMidpoint,
    hourHeight,
    dayStartHour,
    dayEndHour,
    stickyHeaderPx,
  ].join(':');

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !openingHoursReady) return;

    // A first layout pass can report a too-tall clientHeight. Centring 08:00–16:00
    // in that box lands near 02:00, and locking the first scroll keeps it there
    // after the calendar shrinks to its real height. Re-apply until the user scrolls.
    let userAdjusted = false;
    let setting = false;
    let programmaticTop: number | null = null;

    const apply = () => {
      if (userAdjusted || el.clientHeight <= 0) return;
      const next = calendarInitialScrollTop({
        midpointHour: openingHoursMidpoint,
        hourHeight,
        dayStartHour,
        dayEndHour,
        viewportHeight: el.clientHeight,
        stickyHeaderPx,
      });
      if (Math.abs(el.scrollTop - next) < 1) {
        programmaticTop = el.scrollTop;
        return;
      }
      setting = true;
      el.scrollTop = next;
      programmaticTop = el.scrollTop;
      setting = false;
    };

    const onScroll = () => {
      if (setting) return;
      if (programmaticTop != null && Math.abs(el.scrollTop - programmaticTop) < 1) return;
      userAdjusted = true;
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', onScroll);
    };
  }, [
    scrollContextKey,
    openingHoursReady,
    openingHoursMidpoint,
    hourHeight,
    dayStartHour,
    dayEndHour,
    stickyHeaderPx,
  ]);

  const closedHoursOverlays = useMemo(() => {
    const overlays: Array<{ dayIso: string; top: number; height: number }> = [];
    // Per-column overlays own the grey bands when staff columns are actually rendered.
    // An empty columnHours map (or a single selected practitioner with no columns)
    // must still draw day-level closed bands.
    if (staffColumns.length > 0 && columnHoursByDate && columnHoursByDate.size > 0) {
      return overlays;
    }
    for (const day of days) {
      const civilDate = clinicDayKey(day);
      const selectedColumnHours = hoursColumnId
        ? columnHoursByDate?.get(civilDate)?.get(hoursColumnId)
        : undefined;
      const resolved = selectedColumnHours ?? resolvedHoursByDate?.get(civilDate);
      for (const overlay of closedHourOverlays({
        dayStartHour,
        dayEndHour,
        hourHeight,
        hours: resolved ?? bookingHours.get(clinicJsWeekday(day)),
      })) {
        overlays.push({ dayIso: day.toISOString(), ...overlay });
      }
    }
    return overlays;
  }, [
    days,
    bookingHours,
    resolvedHoursByDate,
    columnHoursByDate,
    hoursColumnId,
    staffColumns.length,
    hourHeight,
    dayStartHour,
    dayEndHour,
  ]);

  const dayMinWidth = splitByStaff
    ? Math.max(CALENDAR_DAY_COLUMN_MIN_PX, columns.length * 88)
    : CALENDAR_DAY_COLUMN_MIN_PX;

  function appointmentsFor(day: Date, columnId?: string) {
    const dayAppointments = byDay.get(dayKey(day)) ?? [];
    if (columnPerspective === 'resource') {
      return appointmentsInResourceColumn(dayAppointments, columnId, allocationByAppointment);
    }
    return appointmentsInStaffColumn(dayAppointments, columnId);
  }

  function renderSlots(d: Date, resourceId?: string) {
    return hours.map((h) => (
      <div key={h} className="border-b border-ds-line" style={{ height: `${hourHeight}px` }}>
        {[0, 1].map((half) => {
          const slotMin = dayStartHour * 60 + (h - dayStartHour) * 60 + half * SLOT_MINUTES;
          const isSelected =
            quickSel &&
            quickSel.dayIso === d.toISOString() &&
            quickSel.resourceId === resourceId &&
            slotMin >= quickSel.startMin &&
            slotMin < quickSel.endMin;
          return (
            <div
              key={half}
              className={`block h-1/2 w-full select-none ${
                isSelected ? 'bg-ds-teal-tint' : 'hover:bg-ds-teal-tint/40'
              }`}
              aria-label={`${String(h).padStart(2, '0')}:${half === 0 ? '00' : '30'}`}
              onMouseDown={(e) => {
                if (e.button !== 0 || isDnDActive) return;
                const column = e.currentTarget.closest('[data-calendar-slots]');
                if (!(column instanceof HTMLElement)) return;
                e.preventDefault();
                mouseDownRef.current = true;
                const offsetY = e.clientY - column.getBoundingClientRect().top;
                const startMin = slotStartMinutesFromOffset({
                  offsetY,
                  hourHeight,
                  dayStartHour,
                  dayEndHour,
                });
                const next = {
                  dayIso: d.toISOString(),
                  startMin,
                  endMin: startMin + SLOT_MINUTES,
                  resourceId,
                };
                quickSelRef.current = next;
                setQuickSel(next);
              }}
              onMouseEnter={() => {
                if (!mouseDownRef.current || !quickSelRef.current) return;
                if (quickSelRef.current.dayIso !== d.toISOString()) return;
                if (quickSelRef.current.resourceId !== resourceId) return;
                setQuickSel((prev) => {
                  if (!prev) return null;
                  const next = {
                    ...prev,
                    endMin: Math.max(prev.startMin + SLOT_MINUTES, slotMin + SLOT_MINUTES),
                  };
                  quickSelRef.current = next;
                  return next;
                });
              }}
            />
          );
        })}
      </div>
    ));
  }

  return (
    <div className="flex h-0 min-h-0 flex-1 flex-col overflow-hidden rounded-card bg-ds-card shadow-card">
      <div
        ref={scrollRef}
        data-testid="calendar-time-scroll"
        className="h-0 min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain"
      >
        <div
          className="grid min-w-full"
          style={{
            gridTemplateColumns: `64px repeat(${days.length}, minmax(${dayMinWidth}px, 1fr))`,
          }}
        >
          <div className="sticky left-0 top-0 z-20 border-b border-ds-line bg-ds-card" />
          {days.map((d) => {
            const dow = clinicWeekdayIndex(d);
            const resolved = resolvedHoursByDate?.get(clinicDayKey(d));
            const showClinicDayLabel = !columnHoursByDate || columnHoursByDate.size === 0;
            const exceptionLabel =
              showClinicDayLabel && resolved?.source === 'exception'
                ? resolved.exceptionType === 'closed'
                  ? t('appointments.day_hours.indicator_closed')
                  : resolved.enabled
                    ? t('appointments.day_hours.indicator_open', {
                        hours: (resolved.periods ?? [{ start: resolved.start, end: resolved.end }])
                          .map((p) => `${p.start}–${p.end}`)
                          .join(', '),
                      })
                    : t('appointments.day_hours.indicator_closed')
                : null;
            return (
              <div
                key={d.toISOString()}
                data-testid="day-column-header"
                className={`sticky top-0 z-10 border-b border-l border-ds-line px-s2 py-s2 text-center ${
                  sameClinicDay(d, today) ? 'bg-ds-card' : 'bg-ds-card'
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    onSelectDay?.(d);
                    if (hoursColumnId && onColumnDayHoursClick) {
                      onColumnDayHoursClick(d, hoursColumnId);
                      return;
                    }
                    onDayHoursClick?.(d);
                  }}
                  onContextMenu={(e) => {
                    if (hoursColumnId && onColumnDayHoursClick) {
                      e.preventDefault();
                      onColumnDayHoursClick(d, hoursColumnId);
                      return;
                    }
                    if (!onDayHoursClick) return;
                    e.preventDefault();
                    onDayHoursClick(d);
                  }}
                  className="w-full rounded-ctl px-0.5 hover:bg-ds-neutral-tint focus:outline-none"
                  aria-label={t('appointments.day_hours.open_for_date', { date: d.getDate() })}
                >
                  <div
                    className={cn(
                      TYPE.meta,
                      'leading-none',
                      sameClinicDay(d, today) ? 'text-ds-teal' : 'text-ds-muted',
                    )}
                  >
                    {t(`appointments.weekdays_short.${WEEKDAY_KEYS[dow]}`)}
                  </div>
                  <div
                    className={cn(
                      'mx-auto mt-0.5 inline-flex h-8 min-w-8 items-center justify-center rounded-full text-[18px] font-semibold tabular-nums leading-6',
                      sameClinicDay(d, today) ? 'bg-ds-teal text-ds-on-accent' : 'text-ds-text',
                    )}
                  >
                    {clinicDayNumber(d)}
                  </div>
                  {exceptionLabel && (
                    <div
                      className={`${TYPE.caption} mt-1 truncate text-ds-teal`}
                      data-testid="day-hours-indicator"
                      title={exceptionLabel}
                    >
                      {exceptionLabel}
                    </div>
                  )}
                </button>
                {splitByStaff && (
                  <div
                    className="mt-1 grid gap-px"
                    style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
                  >
                    {columns.map((col) => {
                      const columnHours = columnHoursByDate?.get(clinicDayKey(d))?.get(col.id);
                      const hoursLabel = columnHours
                        ? !columnHours.enabled
                          ? t('appointments.practitioner_hours.indicator_closed')
                          : columnHours.source === 'exception'
                            ? t('appointments.practitioner_hours.indicator_open', {
                                hours: (
                                  columnHours.periods ?? [
                                    { start: columnHours.start, end: columnHours.end },
                                  ]
                                )
                                  .map((period) => `${period.start}–${period.end}`)
                                  .join(', '),
                              })
                            : null
                        : null;
                      const clickable = Boolean(onColumnDayHoursClick && col.id !== 'unassigned');
                      const nameLines = practitionerColumnNameLines(col.name);
                      return (
                        <button
                          key={col.id}
                          type="button"
                          data-testid="calendar-column-header"
                          data-column-id={col.id}
                          aria-disabled={clickable ? undefined : true}
                          tabIndex={clickable ? 0 : -1}
                          onClick={(event) => {
                            if (!clickable) return;
                            event.stopPropagation();
                            onColumnDayHoursClick?.(d, col.id);
                          }}
                          className={cn(
                            'min-w-0 overflow-hidden whitespace-normal px-0.5 text-center text-[10px] font-medium leading-tight text-ds-muted',
                            !clickable && 'cursor-default',
                          )}
                          title={col.subtitle ? `${col.name} · ${col.subtitle}` : col.name}
                        >
                          <span
                            className="block max-w-full whitespace-normal break-words"
                            data-testid="calendar-column-name"
                          >
                            {nameLines.line1}
                          </span>
                          {nameLines.line2 ? (
                            <>
                              {' '}
                              <span
                                className="block max-w-full whitespace-normal break-words"
                                data-testid="calendar-column-name"
                              >
                                {nameLines.line2}
                              </span>
                            </>
                          ) : null}
                          {col.subtitle ? (
                            <div className="truncate text-[9px] font-normal text-ds-muted">
                              {col.subtitle}
                            </div>
                          ) : null}
                          {hoursLabel ? (
                            <div
                              className="truncate text-[9px] font-normal text-ds-teal"
                              data-testid="practitioner-hours-indicator"
                            >
                              {hoursLabel}
                            </div>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="sticky left-0 z-10 bg-ds-card">
            {hours.map((h) => (
              <div
                key={h}
                data-hour={h}
                className={cn(TYPE.caption, 'pr-2.5 text-right tabular-nums text-ds-muted')}
                style={{ height: `${hourHeight}px` }}
              >
                <span className="relative -top-2">{formatHourLabel(h, i18n.language)}</span>
              </div>
            ))}
          </div>

          {days.map((d) => {
            const civilDate = clinicDayKey(d);
            const dayOverlays = closedHoursOverlays.filter((o) => o.dayIso === d.toISOString());
            const subcolumns = splitByStaff
              ? columns
              : [{ id: undefined as string | undefined, name: '' }];
            const perPractitionerOverlay = splitByStaff && columnPerspective === 'practitioner';
            return (
              <div
                key={d.toISOString()}
                className={cn(
                  'relative border-l border-ds-line',
                  sameClinicDay(d, today) && 'bg-ds-today-col',
                  isCalendarWeekend(d) && !sameClinicDay(d, today) && 'bg-ds-sunk',
                )}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${subcolumns.length}, minmax(0, 1fr))`,
                  height: hours.length * hourHeight,
                  backgroundImage:
                    'repeating-linear-gradient(to bottom, transparent 0, transparent calc(var(--hour-h) - 1px), var(--line) calc(var(--hour-h) - 1px), var(--line) var(--hour-h))',
                  ['--hour-h' as string]: `${hourHeight}px`,
                }}
              >
                {!perPractitionerOverlay &&
                  dayOverlays.map((overlay, idx) => (
                    <div
                      key={`overlay-${d.toISOString()}-${idx}`}
                      data-testid="closed-hours-overlay"
                      className={`pointer-events-none absolute left-0 right-0 ${CALENDAR_CLOSED_OVERLAY_CLASS}`}
                      style={{ top: `${overlay.top}px`, height: `${overlay.height}px`, zIndex: 0 }}
                    />
                  ))}
                {subcolumns.map((col) => {
                  const resourceId = col.id === 'unassigned' ? undefined : col.id;
                  const columnHours =
                    (col.id ?? hoursColumnId)
                      ? columnHoursByDate?.get(civilDate)?.get((col.id ?? hoursColumnId) as string)
                      : undefined;
                  const fallbackHours =
                    perPractitionerOverlay && col.id
                      ? (resolvedHoursByPractitionerDate?.get(
                          practitionerDateHoursKey(col.id, civilDate),
                        ) ?? resolvedHoursByDate?.get(civilDate))
                      : undefined;
                  const overlayHours = columnHours ?? fallbackHours;
                  const columnOverlays =
                    overlayHours !== undefined
                      ? closedHourOverlays({
                          dayStartHour,
                          dayEndHour,
                          hourHeight,
                          hours: overlayHours ?? bookingHours.get(clinicJsWeekday(d)),
                        })
                      : [];
                  return (
                    <DroppableColumn key={col.id ?? 'day'} date={d} resourceId={resourceId}>
                      {columnOverlays.map((overlay, idx) => (
                        <div
                          key={`col-overlay-${col.id}-${d.toISOString()}-${idx}`}
                          data-testid="practitioner-hours-overlay"
                          data-column-id={col.id}
                          className={`pointer-events-none absolute left-0 right-0 ${CALENDAR_CLOSED_OVERLAY_CLASS}`}
                          style={{
                            top: `${overlay.top}px`,
                            height: `${overlay.height}px`,
                            zIndex: 0,
                          }}
                        />
                      ))}
                      {renderSlots(d, resourceId)}
                      <div
                        data-testid="calendar-overlap-layer"
                        className="pointer-events-none absolute inset-0 z-[1]"
                      >
                        <StaffColumnAppointments
                          appointments={appointmentsFor(d, col.id)}
                          metaById={metaById}
                          conflictAptId={conflictAptId}
                          language={locale}
                          hourHeight={hourHeight}
                          dayStartHour={dayStartHour}
                          dayEndHour={dayEndHour}
                          showClinician={showClinicianOnCards}
                          onAppointmentClick={onAppointmentClick}
                        />
                      </div>
                    </DroppableColumn>
                  );
                })}
                {(groupSessionsByDay.get(dayKey(d)) ?? []).map((gs) => (
                  <GroupSessionBlock
                    key={gs.id}
                    session={gs}
                    onClick={() => onGroupSessionClick(gs.id)}
                    language={locale}
                    hourHeight={hourHeight}
                    dayStartHour={dayStartHour}
                    dayEndHour={dayEndHour}
                  />
                ))}
                {sameClinicDay(d, today) &&
                  (() => {
                    const top = calendarNowLineTop({
                      now,
                      dayStartHour,
                      dayEndHour,
                      hourHeight,
                    });
                    if (top == null) return null;
                    return (
                      <div
                        className="pointer-events-none absolute left-0 right-0 z-[2] h-0.5 bg-ds-red"
                        style={{ top: `${top}px` }}
                        aria-label={`${t('appointments.now')} ${formatHourLabel(now.getHours(), i18n.language)}`}
                        data-testid="calendar-now-line"
                      >
                        <span className="absolute -left-1.5 -top-1 h-2.5 w-2.5 rounded-full bg-ds-red" />
                      </div>
                    );
                  })()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StaffColumnAppointments({
  appointments,
  metaById,
  conflictAptId,
  language,
  hourHeight,
  dayStartHour,
  dayEndHour,
  showClinician,
  onAppointmentClick,
}: {
  appointments: AppointmentListItem[];
  metaById: Map<string, CalendarAppointmentMeta>;
  conflictAptId: string | null;
  language: string;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
  showClinician: boolean;
  onAppointmentClick: (id: string) => void;
}) {
  const timedAppointments = appointments.filter(
    (appointment) => !isAllDayCalendarBlock(appointment),
  );
  const allDayBlocks = appointments.filter((appointment) => isAllDayCalendarBlock(appointment));
  const overlapLayout = layoutOverlappingAppointments(timedAppointments.map(appointmentToInterval));
  const appointmentById = new Map(
    timedAppointments.map((appointment) => [appointment.id, appointment]),
  );

  return (
    <>
      {allDayBlocks.map((appointment) => {
        const box = allDayColumnBox({ dayStartHour, dayEndHour, hourHeight });
        return (
          <button
            key={appointment.id}
            type="button"
            data-testid="calendar-all-day-block"
            className="pointer-events-auto absolute left-1 right-1 z-[3] overflow-hidden rounded border border-ds-line-strong bg-ds-sunk px-1 pt-1 text-left text-xs font-semibold text-ds-text"
            style={{
              top: box.top,
              height: Math.max(20, box.height - 2),
              borderLeftWidth: 3,
              borderLeftColor: appointment.service_color ?? '#9CA3AF',
              backgroundImage:
                'repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(15, 23, 42, 0.08) 5px, rgba(15, 23, 42, 0.08) 7px)',
            }}
            onClick={(event) => {
              event.stopPropagation();
              onAppointmentClick(appointment.id);
            }}
          >
            {calendarBlockTitle(appointment)}
          </button>
        );
      })}
      {timedAppointments.map((appointment) => {
        const overlapPlacement = overlapLayout.placements.get(appointment.id);
        if (!overlapPlacement || overlapPlacement.hidden) return null;
        return (
          <DraggableAppointmentBlock
            key={appointment.id}
            appointment={appointment}
            meta={metaById.get(appointment.id)}
            overlapPlacement={overlapPlacement}
            onClick={() => onAppointmentClick(appointment.id)}
            language={language}
            isConflict={conflictAptId === appointment.id}
            hourHeight={hourHeight}
            dayStartHour={dayStartHour}
            dayEndHour={dayEndHour}
            showClinician={showClinician}
          />
        );
      })}
      {overlapLayout.overflowGroups.map((group) => {
        const overflowAppointments = group.ids
          .map((id) => appointmentById.get(id))
          .filter((item): item is AppointmentListItem => item != null);
        const geometry = timedBlockGeometry({
          startsAt: new Date(group.startMs),
          endsAt: new Date(group.endMs),
          hourHeight,
          dayStartHour,
          dayEndHour,
        });
        if (!geometry || overflowAppointments.length === 0) return null;
        return (
          <CalendarOverlapOverflow
            key={`overflow-${group.ids.join('-')}`}
            appointments={overflowAppointments}
            top={geometry.top}
            height={geometry.height}
            language={language}
            onOpenAppointment={onAppointmentClick}
          />
        );
      })}
    </>
  );
}

function DroppableColumn({
  date,
  resourceId,
  children,
}: {
  date: Date;
  resourceId?: string;
  children: ReactNode;
}) {
  const columnId = `col-${date.toISOString()}-${resourceId ?? 'day'}`;
  const { setNodeRef, isOver } = useDroppable({
    id: columnId,
    data: { date: date.toISOString(), resourceId } satisfies DropData,
  });
  return (
    <div
      ref={setNodeRef}
      data-calendar-slots="true"
      className={`relative h-full w-full min-w-0 border-l border-ds-line first:border-l-0 ${
        isOver ? 'bg-ds-teal-tint/40' : ''
      }`}
    >
      {children}
    </div>
  );
}

function DraggableAppointmentBlock({
  appointment,
  meta,
  overlapPlacement,
  onClick,
  language,
  isConflict,
  hourHeight,
  dayStartHour,
  dayEndHour,
  showClinician,
}: {
  appointment: AppointmentListItem;
  meta?: CalendarAppointmentMeta;
  overlapPlacement: OverlapPlacement;
  onClick: () => void;
  language: string;
  isConflict: boolean;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
  showClinician: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `reschedule-${appointment.id}`,
    data: { kind: 'reschedule', appointment } satisfies DragData,
  });

  const start = new Date(appointment.starts_at);
  const end = new Date(appointment.ends_at);
  const geometry = timedBlockGeometry({
    startsAt: start,
    endsAt: end,
    hourHeight,
    dayStartHour,
    dayEndHour,
  });
  if (!geometry) return null;

  return (
    <div
      data-testid="calendar-appointment-slot"
      data-appointment-id={appointment.id}
      data-overlap-column={overlapPlacement.column}
      data-overlap-columns={overlapPlacement.columnCount}
      className="pointer-events-auto"
      style={{
        top: `${geometry.top}px`,
        height: `${geometry.height}px`,
        zIndex: isDragging ? 50 : 1,
        ...overlapBoxStyle(overlapPlacement),
      }}
    >
      <AppointmentCalendarCard
        appointment={appointment}
        meta={meta}
        language={language}
        height={geometry.height}
        fillParent
        isConflict={isConflict}
        isDragging={isDragging}
        setNodeRef={setNodeRef}
        dragHandleProps={{ ...attributes, ...listeners }}
        style={{
          transform: CSS.Translate.toString(transform),
          height: '100%',
          touchAction: 'none',
        }}
        onOpenAppointment={onClick}
        showClinician={showClinician}
      >
        <ResizeHandle appointment={appointment} />
      </AppointmentCalendarCard>
    </div>
  );
}

function ResizeHandle({ appointment }: { appointment: AppointmentListItem }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `resize-${appointment.id}`,
    data: { kind: 'resize-end', appointment } satisfies DragData,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      className="hover:bg-ds-text/10 absolute bottom-0 left-0 right-0 h-1.5 cursor-s-resize rounded-b"
      aria-label={t('appointments.resize_handle')}
      style={{ touchAction: 'none' }}
    />
  );
}

export function AppointmentGhost({
  appointment,
  language,
  hourHeight,
}: {
  appointment: AppointmentListItem;
  language: string;
  hourHeight: number;
}) {
  const start = new Date(appointment.starts_at);
  const end = new Date(appointment.ends_at);
  const durationMinutes = Math.max(15, (end.getTime() - start.getTime()) / 60_000);
  const height = Math.max(18, (durationMinutes / 60) * hourHeight - 2);
  const { showTime } = calendarBlockLineVisibility(height);

  return (
    <div
      className="w-32 cursor-grabbing overflow-hidden rounded-[8px] border border-ds-line-strong bg-ds-card px-1.5 py-1 text-[11px] leading-tight text-ds-text shadow-pop"
      style={{
        height: `${height}px`,
        opacity: 0.9,
        borderLeft: `4px solid ${appointmentJournalRailColor('draft')}`,
      }}
    >
      <div className="truncate font-semibold">{appointment.patient_name}</div>
      {showTime && (
        <div className="mt-0.5 truncate text-[10px] text-ds-muted">
          {formatAppointmentTimeRange(start, end, language, CLINIC_TIMEZONE)}
        </div>
      )}
    </div>
  );
}

function GroupSessionBlock({
  session,
  onClick,
  language,
  hourHeight,
  dayStartHour,
  dayEndHour,
}: {
  session: GroupSessionListItem;
  onClick: () => void;
  language: string;
  hourHeight: number;
  dayStartHour: number;
  dayEndHour: number;
}) {
  const { t } = useTranslation();
  const start = new Date(session.starts_at);
  const end = new Date(session.ends_at);
  const geometry = timedBlockGeometry({
    startsAt: start,
    endsAt: end,
    hourHeight,
    dayStartHour,
    dayEndHour,
  });
  if (!geometry) return null;

  const block = clinicColorBlockAppearance(session.color);
  const timeLabel = formatAppointmentTimeRange(start, end, language, CLINIC_TIMEZONE);
  const spotsAvailable = session.max_participants - session.current_participants;
  const isFull = spotsAvailable <= 0;

  return (
    <div
      onClick={onClick}
      className="absolute left-1 right-1 z-[2] cursor-pointer overflow-hidden rounded-lg px-2 py-1 text-left text-xs shadow-card ring-1 ring-ds-line-strong transition hover:opacity-90"
      style={{
        top: `${geometry.top}px`,
        height: `${geometry.height}px`,
        backgroundColor: block.background,
        color: block.color,
        borderLeft: block.accentOnly ? `4px solid ${block.accent}` : undefined,
        opacity: session.status === 'cancelled' ? 0.5 : 1,
      }}
      title={t('appointments.click_to_view')}
    >
      <div className="flex items-center gap-1">
        <Users className="h-3 w-3 flex-shrink-0" />
        <div className="flex-1 truncate font-semibold leading-tight">{session.name}</div>
      </div>
      <div className="opacity-90">{timeLabel}</div>
      <div className="flex items-center gap-1 text-[10px] opacity-80">
        <span>
          {session.current_participants}/{session.max_participants}
        </span>
        {isFull && <span className="font-semibold">(FULL)</span>}
      </div>
    </div>
  );
}
